import {
  V2PowerRoomConfigPowerSystemConfigurationTreeDataPostResponse,
  V2PowerRoomConfigPowerSystemConfigurationTreePostResponse,
} from '@maxtropy/device-customer-apis-v2';

export function levelOrderForest(roots: any[]) {
  if (!roots || roots.length === 0) return [];
  const result = [];
  const queue = [...roots]; // 多个根节点

  while (queue.length) {
    const len = queue.length;
    const level = [];

    for (let i = 0; i < len; i++) {
      const node = queue.shift();
      level.push(node);
      if (node.children?.length) {
        queue.push(...node.children);
      }
    }

    result.push(level);
  }

  return result;
}

// 有子节点的（children 数组存在且长度 > 0）排在前面
// 没有子节点的（children 不存在或长度为 0）排在后面
export function groupByChildren<T extends Record<string, any>>(
  arr: T[],
  childKey: keyof T = 'children' as keyof T
): { withChildren: T[]; withoutChildren: T[] } {
  const withChildren: T[] = [];
  const withoutChildren: T[] = [];

  arr.forEach(item => {
    const children = item[childKey];
    if (Array.isArray(children) && children.length > 0) {
      withChildren.push(item);
    } else {
      withoutChildren.push(item);
    }
  });

  return { withChildren, withoutChildren };
}

export function findItemById(data?: V2PowerRoomConfigPowerSystemConfigurationTreePostResponse, targetId?: number) {
  let result: any = null;

  (data?.powerSystemList ?? []).forEach(circuit => {
    (circuit?.powerSystemBusPaiList ?? []).forEach(busPai => {
      function dfs(node: any, parent = null) {
        if (result) return; // 已经找到就直接返回

        if (node?.id === targetId) {
          result = {
            circuitItem: circuit,
            busPaiItem: busPai,
            item: node,
            parentItem: parent,
          };
          return;
        }

        if (node.children) {
          node.children.forEach((child: any) => dfs(child, node));
        }
      }

      (busPai?.powerSystemConfigurationTree ?? []).forEach(root => dfs(root));
    });
  });

  return result;
}

export function getTreeCircuitIds(data?: V2PowerRoomConfigPowerSystemConfigurationTreePostResponse) {
  const idSet = new Set();

  function collect(node: any) {
    if (node.powerCircuitList && Array.isArray(node.powerCircuitList)) {
      node.powerCircuitList.forEach((id: number) => idSet.add(id));
    }
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child: any) => collect(child));
    }
  }

  (data?.powerSystemList ?? []).forEach(circuit => {
    (circuit?.powerSystemBusPaiList ?? []).forEach(busPai => {
      (busPai?.powerSystemConfigurationTree ?? []).forEach(root => collect(root));
    });
  });

  return Array.from(idSet) as number[];
}

// 监测回路
export function getTreeMonitorCircuitIds(data?: V2PowerRoomConfigPowerSystemConfigurationTreePostResponse) {
  const idSet = new Set();

  function collect(node: any) {
    if (node.monitorCircuitList && Array.isArray(node.monitorCircuitList)) {
      node.monitorCircuitList.forEach((id: number) => idSet.add(id));
    }
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child: any) => collect(child));
    }
  }

  (data?.powerSystemList ?? []).forEach(circuit => {
    (circuit?.powerSystemBusPaiList ?? []).forEach(busPai => {
      (busPai?.powerSystemConfigurationTree ?? []).forEach(root => collect(root));
    });
  });

  return Array.from(idSet) as number[];
}

// 数据组合
export function mergeCircuitDataToItemList(
  idList: V2PowerRoomConfigPowerSystemConfigurationTreeDataPostResponse['list'],
  monitorIdlist: V2PowerRoomConfigPowerSystemConfigurationTreeDataPostResponse['list'],
  data?: V2PowerRoomConfigPowerSystemConfigurationTreePostResponse
) {
  const idsMap = new Map();
  const monitorIdsMap = new Map();
  idList?.forEach(item => idsMap.set(item.circuitId, item));
  monitorIdlist?.forEach(item => monitorIdsMap.set(item.circuitId, item));

  function attach(node: any, circuitId?: number) {
    if (Array.isArray(node.powerCircuitList) && node.powerCircuitList.length > 0) {
      node.id = node.id + '-' + circuitId;
      node.itemList = node.powerCircuitList.map((id: number) => {
        const match = idsMap.get(id);
        if (match) {
          return { ...match }; // 包含 circuitId 和其他字段
        } else {
          return { circuitId: id }; // 没匹配到时保留 id
        }
      });
    }
    if (Array.isArray(node.monitorCircuitList) && node.monitorCircuitList.length > 0) {
      node.id = node.id + '-' + circuitId;
      node.monitorItemList = node.monitorCircuitList.map((id: number) => {
        const match = monitorIdsMap.get(id);
        if (match) {
          return { ...match }; // 包含 circuitId 和其他字段
        } else {
          return { circuitId: id }; // 没匹配到时保留 id
        }
      });
    }

    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach((child: any) => attach(child, circuitId));
    }
  }

  data?.powerSystemList?.forEach(circuit => {
    circuit?.powerSystemBusPaiList?.forEach(busPai => {
      busPai?.powerSystemConfigurationTree?.forEach(root => attach(root, circuit.circuitId));
    });
  });

  return data;
}
